import * as React from "react";
import { observer } from "mobx-react";
import {
  UitkCard,
  UitkCardMedia,
  UitkCardLink,
  UitkCardTextSegment,
  UitkCardSectionSeparatorType,
} from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";
import Offers = CampaignRecommendationsWithOffersQuery.Offers;
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkVipBadge } from "uitk-react-badge";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";

export interface PackageInventoryCardProps {
  offer: Offers;
  cardIndex: number;
  campaignId: string;
  moduleName: string;
  withBorder?: boolean;
}

export const PackageInventoryCard = observer(
  ({ offer, cardIndex, campaignId, moduleName, withBorder }: PackageInventoryCardProps) => {
    if (offer.__typename !== "PackageOfferItem") {
      return null;
    }

    const { hotelOfferItem, flightOfferItem, packageOffer } = offer;
    const { formatText } = useLocalization();

    return (
      <UitkCard
        className="PackageInventoryCard uitk-flex-justify-content-flex-start uitk-flex-column"
        withBorder={withBorder}
      >
        <UitkCardMedia>
          <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
            {hotelOfferItem?.vipMessage && (
              <UitkVipBadge
                className="all-t-margin-three all-l-margin-three hotelBadge"
                text={hotelOfferItem.vipMessage}
              />
            )}
            <LazyLoadedImage src={offer.image.url} alt={offer.image.description} />
          </UitkFigure>
          <UitkCardLink>
            <TrackedLink
              moduleName={moduleName}
              action={Action.CLICK}
              href={packageOffer?.actionLink.href}
              rfrr={`card-${campaignId}-${cardIndex}`}
            >
              {packageOffer?.actionLink.text}
            </TrackedLink>
          </UitkCardLink>
        </UitkCardMedia>
        <div className="uitk-card-content uitk-cell">
          <UitkCardTextSegment>
            <div className="uitk-type-paragraph-300">{offer.description}</div>
            <h3 className="truncate all-b-padding-half uitk-type-heading-500">{hotelOfferItem?.name}</h3>
            <div className="uitk-type-300">{hotelOfferItem?.location}</div>
            <div className="uitk-type-200 all-t-padding-three">
              <img
                className="airlinesIcon all-r-margin-one"
                src={flightOfferItem?.airlineImage?.url}
                alt={flightOfferItem?.airlineImage?.description}
              />
              <span>{flightOfferItem?.airlineName}</span>
            </div>
            {flightOfferItem && (
              <div className="uitk-type-300 all-b-padding-one">
                {formatText(
                  "merchCampaign.packageOffer.flightOffer.name",
                  flightOfferItem.originCity,
                  flightOfferItem.originAirportCode,
                  flightOfferItem.destinationCity,
                  flightOfferItem.destinationAirportCode
                )}
              </div>
            )}
          </UitkCardTextSegment>
          <UitkCardTextSegment separator={UitkCardSectionSeparatorType.TOP}>
            <UitkLayoutFlex justifyContent="space-between">
              <>
                <div className="uitk-type-200">{offer.dates}</div>
                {packageOffer?.price.value && (
                  <div className="uitk-type-right">
                    <span className="uitk-type-100 all-r-padding-one price-prefix">
                      {packageOffer.price.prefixText}
                    </span>
                    <span className="uitk-type-heading-600">{packageOffer.price.value}</span>
                    <div className="uitk-type-100">{packageOffer.price.qualifierText}</div>
                  </div>
                )}
                {packageOffer?.price.checkPriceText && (
                  <div className="uitk-type-right">
                    <a
                      href={packageOffer?.actionLink.href}
                      className="uitk-link uitk-type-300 uitk-type-bold price-prefix"
                    >
                      {packageOffer?.price.checkPriceText}
                    </a>
                  </div>
                )}
              </>
            </UitkLayoutFlex>
          </UitkCardTextSegment>
        </div>
      </UitkCard>
    );
  }
);
